import { useCallback, useEffect, useRef } from 'react'
import { useQueryParams } from '@fs/zion-router'
import { setDynamicQueryParams } from '../shared/NavigationProvider/useDynamicNavigation'
import { convertFormToQuerySync } from './formUtils'
import { parseQueryParams } from './queryParamUtils'

// Debounce delay for URL sync
const DEBOUNCE_DELAY = 300

export const useFormQuerySync = ({ onFormUpdate, onTouchedUpdate, getCurrentValues, getCurrentTouched }) => {
  const { query } = useQueryParams()
  const syncTimeoutRef = useRef()
  const isUpdatingFromURLRef = useRef(false)

  // Initialize form from URL query params
  useEffect(() => {
    const initialValues = parseQueryParams(query)

    // Set initial values if any query params exist
    if (Object.keys(query).length > 0) {
      isUpdatingFromURLRef.current = true
      onFormUpdate(initialValues)

      // Mark fields as touched if they exist in the URL
      const touchedFields = {}
      Object.keys(initialValues).forEach((key) => {
        if (query[key]) {
          touchedFields[key] = true
        }
      })
      onTouchedUpdate(touchedFields)

      setTimeout(() => {
        isUpdatingFromURLRef.current = false
      }, 100)
    }
  }, [query, onFormUpdate, onTouchedUpdate])

  // Sync form values to URL with debounce (now only updates dynamic params)
  const syncToURL = useCallback(() => {
    if (isUpdatingFromURLRef.current) return

    // Clear existing timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
    }

    // Debounce the sync operation
    syncTimeoutRef.current = setTimeout(() => {
      const currentValues = getCurrentValues()
      const currentTouched = getCurrentTouched()

      const queryUpdates = convertFormToQuerySync(currentValues, currentTouched)

      // Convert to URLSearchParams string format for dynamic params
      const params = new URLSearchParams()
      Object.entries(queryUpdates).forEach(([key, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            params.append(key, value.join(','))
          } else {
            params.append(key, String(value))
          }
        }
      })
    }, DEBOUNCE_DELAY)
  }, [getCurrentValues, getCurrentTouched])

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [])

  return { syncToURL }
}

export const useFormSubmit = ({ getCurrentValues, getCurrentTouched }) => {
  const { query, setQuery } = useQueryParams()

  const handleSubmit = useCallback(() => {
    const currentValues = getCurrentValues()
    const currentTouched = getCurrentTouched()

    const formQueryUpdates = convertFormToQuerySync(currentValues, currentTouched)

    // Get form field names to preserve non-form query parameters
    const formFieldNames = new Set([
      'forceCountry',
      'forcePreferences',
      'forcePreferencesSelected',
      'forcePermissions',
      'forcePermissionsSelected',
      'forceRussianAccess',
      'forceTempleViewable',
      'forceSignedOut',
      'forceChurchMembership',
    ])

    // Preserve existing non-form query parameters
    const preservedParams = {}
    Object.entries(query).forEach(([key, value]) => {
      if (!formFieldNames.has(key)) {
        preservedParams[key] = value
      }
    })

    // Merge preserved params with form updates
    const finalQueryUpdates = { ...preservedParams, ...formQueryUpdates }

    // Update query params with both preserved and form data
    setQuery(finalQueryUpdates)

    // Also update dynamic query params in CHEF
    const params = new URLSearchParams()
    Object.entries(currentValues).forEach(([key, value]) => {
      if (
        key === 'forcePermissions' ||
        key === 'forcePreferencesSelected' ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && Object.keys(value).length === 0)
      )
        return
      if (currentValues.forceSignedOut === false && key === 'forcePermissionsSelected') {
        let permissions = ''
        Object.entries(value).forEach(([perm, isSelected]) => {
          const [context, permission] = perm.includes('::') ? perm.split('::') : [undefined, perm]
          permissions += `${context ? `${isSelected ? '' : '!'}${context}::` : ''}${permission},`
        })
        params.append('forcePermissions', permissions.slice(0, -1))
      } else if (currentValues.forceSignedOut === false && key === 'forcePreferences' && Array.isArray(value)) {
        const Preferences = value.filter((v) => currentValues.forcePreferencesSelected[v])
        params.append(key, Preferences.join(','))
      } else if (
        currentValues.forceSignedOut === false ||
        key === 'forceCountry' ||
        (currentValues.forceSignedOut === true && key === 'forceSignedOut')
      ) {
        params.append(key, String(value))
      }
    })
    setDynamicQueryParams(params.toString())
  }, [getCurrentValues, getCurrentTouched, query, setQuery])

  return { handleSubmit }
}
